import * as React from 'react';
import axios from 'axios';
import styled from '@independent-software/typeui/styles/Theme'
import * as domtoimage from 'dom-to-image';
import { CartesianGrid, Legend, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Line } from 'recharts';
import { format, parse } from 'date-fns';

import { Panel } from '@independent-software/typeui/controls/Panel';
import { Form } from '@independent-software/typeui/controls/Form';
import { Dropdown } from '@independent-software/typeui/controls/Dropdown';
import { Message } from '@independent-software/typeui/controls/Message';

import { App } from '../../App';
import { AuthContext } from '../../AuthContext';
import { FilterContext } from '../../FilterContext';
import { Container, Content, BottomBar, IconBar } from '../../modules';
import { Query } from '../../services';
import { Parameter, Station, StationFactory } from '../../resource';
import { CustomTooltip } from './CustomTooltip';
import { Colors } from './ChartColors';
import { Export, TExportFormats } from './Export';
import { Button } from '@independent-software/typeui/controls/Button';
import { useNavigate } from 'react-router-dom';

interface ISerie {
  name: string;
  color: string;
  data: any[];
}

const Analysis = () => {
  const auth = React.useContext(AuthContext);
  const filter = React.useContext(FilterContext);
  const wrapperRef = React.useRef(null as HTMLDivElement);
  const navigate = useNavigate();

  const [series, setSeries] = React.useState([] as ISerie[]);
  const [parameter, setParameter] = React.useState(null as Parameter);
  const [parametersList, setParametersList] = React.useState([] as Parameter[]);
  const [stations, setStations] = React.useState([] as Station[]);
  const [stationsList, setStationsList] = React.useState([] as Station[]);
  
  React.useEffect(() => {
    handleParameterSearch();
    handleStationSearch();
  }, []);

  const handleExport = (format: TExportFormats) => {
    // Use export format to select matching function from domtoimage:
    let f = null;    
    switch(format) {
      case "png": f = (domtoimage as any).toPng; break; 
      case "jpg": f = (domtoimage as any).toJpeg; break;
      case "svg": f = (domtoimage as any).toSvg; break;
      default: return; /* unsupported format */
    }
    // Call export function, then use FileSaver to save to file.
    f(wrapperRef.current, { bgcolor: '#ffffff', cacheBust: true })
    .then(function (dataUrl:any) {
        window.saveAs(dataUrl, `chart.${format}`);
    });
  }  

  const handleParameterSearch = (q?: string) => {
    // Retrieve a list of parameters:
    let url = `${App.config.apiURL}parameters/system?access_token=${auth.access_token}&order=name`;
    url += "&system[]=in-" + filter.activeSystems.join(',');
    if(q) url = url + `&q[]=like-${q}`;
    axios.get(url)
    .then((res) => setParametersList(res.data.data));
  }

  const handleChangeParameter = (_parameter: Parameter) => {
    setParameter(_parameter);
  }

  React.useEffect(() => loadResults(), [parameter]);

  const handleStationSearch = (q?: string) => {
    // Retrieve a list of stations:
    const query = new Query('name', 'asc');
    query.setFilter('system', 'in', filter.activeSystems.join(','));
    if (q) query.setFilter('q', 'like', q);
    StationFactory.getSome(auth, 0, 30, query)
      .then((res) => setStationsList(res.items));
  }

  const handleChangeStation = (_stations: Station[]) => {
    setStations([..._stations]);
  }

  React.useEffect(() => loadResults(), [stations]);

  const loadResults = () => {
    if(parameter == null || stations.length == 0) {
      setSeries([]);
      return;
    }

    const promises = stations.map((station, idx) => {
      return new Promise((resolve, reject) => {
        const query = new Query("value", "asc");
        query.setFilter("parameter", "eq", parameter.id);
        query.setFilter("station", "eq", station.id);
        // Replaced this with manual join, as this produces multiple system[]= entries.
        //query.setFilter('system', 'in', filter.activeSystems);
        let queryUrl = query.toUrl();
        if(queryUrl) queryUrl = '&' + queryUrl;    
        queryUrl += '&system[]=in-' + filter.activeSystems.join(',');
        axios.get(`${App.config.apiURL}results/chart?access_token=${auth.access_token}${queryUrl}`)
        .then(res => {
          resolve({ name: station.name, color: Colors[idx % Colors.length], data: res.data.data });
        });
      });
    });

    Promise.all(promises)
    .then((series: ISerie[]) => {
      setSeries(series);
    });
  }

  const getTicks = (data: ISerie[], numTicks: number): number[] => {
    let minStamp: number = null;
    let maxStamp: number = null;
    data.forEach(s => {
      if(s.data.length < 2) return;
      minStamp = minStamp ? Math.min(minStamp, s.data[0].date) : s.data[0].date;
      maxStamp = maxStamp ? Math.max(maxStamp, s.data[s.data.length-1].date) : s.data[s.data.length-1].date;
    });

    if(minStamp == null || maxStamp == 0) return [];

    const ticks: number[] = [];
    for(let i = 0; i < numTicks; i++) {
      let date = Math.floor((maxStamp - minStamp)/(numTicks-1) * i + minStamp);
      ticks.push(date);
    }
    return ticks;
  }

  const chartReady = (): boolean => parameter != null && stations.length > 0;

  const filterPanel = 
    <>
      <Panel.Content>
        <Form.Uncontrolled label="Parameter" hint="Select parameter">
          <Dropdown onSearch={handleParameterSearch} name="parameter" fluid value={parameter} data={parametersList} placeholder="Parameter" label={(item:Parameter) => item.name} onChange={handleChangeParameter}>
            <Dropdown.Column weight={1}>{(item:Parameter) => item.name}</Dropdown.Column>
          </Dropdown>
        </Form.Uncontrolled>
        <Form.Uncontrolled label="Station" hint="Select station">
          <Dropdown multiple onSearch={handleStationSearch} name="station" fluid value={stations} data={stationsList} placeholder="Station" label={(item:Station) => item.name} onChange={handleChangeStation}>
            <Dropdown.Column weight={1}>{(item:Station) => item.name}</Dropdown.Column>
          </Dropdown>
        </Form.Uncontrolled>
      </Panel.Content>
      <Panel.Footer>
        <Export disabled={!chartReady()} onExport={handleExport}/>
      </Panel.Footer>
    </>

  if(filter.activeSystems.length == 0) {
    return (
      <Container>
        <Underlay/>
        <Overlay>
          <Message type="warning">
            <Message.Content>
            No systems are currently selected. 
            </Message.Content>
            <Message.Content>
              <div style={{textAlign:'right'}}>
                <Button primary onClick={() => navigate("/")}>Select systems</Button>
              </div>
            </Message.Content>
          </Message>
        </Overlay> 
      </Container>     
    )    
  }

  return (
    <Container>
      <>
        <Content>
          
          <IconBar>
            <Panel.Icon icon="tools" width={300}>
              {filterPanel}
            </Panel.Icon>
          </IconBar>

          {!chartReady() &&
            <>
              <Underlay/>
              <Overlay>
                <Message type="warning">
                  Please select a parameter and a station.
                </Message>
              </Overlay>
            </>}

          {chartReady() &&
          <ChartHolder ref={wrapperRef}>
            <ResponsiveContainer width="100%" height="95%">
              <LineChart 
                width={500} height={500} margin={{ top: 30, right: 40, left: 80, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <Legend/>
                <XAxis
                  dataKey="date" type="number" scale="time" allowDuplicatedCategory={false} domain={['dataMin', 'dataMax']}
                  ticks={getTicks(series, 8)}
                  tickFormatter={(stamp) => format(parse(stamp, "t", new Date()), "d MMM yy")}
                  interval={0}
                  padding={{ left: 50, right: 50 }}
                />
                <YAxis
                  domain={["auto", "auto"]}
                  mirror={true}
                  label={{
                    value: parameter.unit,
                    position: 'left',
                    offset: 16,
                    angle: -90
                  }}
                />
                {series.map((s, index) =>
                  <Line key={index} type="linear" name={s.name} data={s.data} dataKey="value" strokeWidth={2} stroke={s.color} fill={"white"} activeDot={{ r: 8 }}/>
                )}
                <Tooltip isAnimationActive={false} content={<CustomTooltip parameter={parameter}/>} />
              </LineChart>
            </ResponsiveContainer>
          </ChartHolder>}
          
        </Content>
        <BottomBar>
        </BottomBar>
      </>
    </Container>
  );
}

// Needed to get a reference for domtoimage.
const ChartHolder = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
`

const Underlay = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: #333;
  z-index: 1;
  opacity: 0.5;
`

const Overlay = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  left: 10%;
  top: 0;
  right: 10%;
  bottom: 0;
  background: transparent;
  z-index: 1;
`

export { Analysis }
